import { parsePhoneNumber } from "libphonenumber-js";
import {
  AlertTriangle,
  ArrowDownToLine,
  Banknote,
  CheckCircle2,
  ClipboardCheck,
  Layers,
  QrCode,
  Send,
  XCircle,
  type LucideIcon,
} from "lucide-react";
import { useMemo, useState } from "react";

import { useAuth } from "@/modules/auth/hooks/useAuth";
import { ColocaButton } from "@/shared/components/buttons/ColocaButton";
import { ColocaAlertDialog } from "@/shared/components/feedback/ColocaAlertDialog";
import { NoAccessView } from "@/shared/components/NoAccessView";
import { NotificationGroup } from "@/shared/components/notifications/NotificationGroup";
import { NotificationPhoneField } from "@/shared/components/notifications/NotificationPhoneField";
import { NotificationTypeCard } from "@/shared/components/notifications/NotificationTypeCard";
import { ProductNotificationsLayout } from "@/shared/components/notifications/ProductNotificationsLayout";
import { renderNotificationBubbleBody } from "@/shared/components/notifications/renderNotificationBubbleBody";
import { WhatsAppBubble } from "@/shared/components/notifications/WhatsAppBubble";
import { WhatsAppIcon } from "@/shared/components/notifications/WhatsAppIcon";
import type { WalletsTranslations } from "@/shared/lang/interfaces/wallets";
import { useTranslations } from "@/shared/lang/useTranslations";
import { cn } from "@/shared/lib/utils";

import { usePaymentNotificationPreferences } from "../hooks/usePaymentNotificationPreferences";
import { useRuntimeWallets } from "../hooks/useRuntimeWallets";
import type { PaymentNotificationEvent } from "../services/paymentNotifications.service";

type WalletsKey = Extract<keyof WalletsTranslations, `notifications_${string}`>;

interface PaymentNotificationDefinition {
  event: PaymentNotificationEvent;
  icon: LucideIcon;
  iconBg: string;
  nameKey: WalletsKey;
  descKey: WalletsKey;
  titleKey: WalletsKey;
  bodyKey: WalletsKey;
  /** Registered with the messaging service, but nothing in Coloca produces it yet. */
  comingSoon?: boolean;
}

interface PaymentNotificationGroup {
  titleKey: WalletsKey;
  descKey: WalletsKey;
  notifications: PaymentNotificationDefinition[];
}

// Bre-B has its own group because its messages say how the money moved (QR or key, with the key holder).
const GROUPS: PaymentNotificationGroup[] = [
  {
    titleKey: "notifications_group_collections_title",
    descKey: "notifications_group_collections_desc",
    notifications: [
      {
        event: "payin_confirmed",
        icon: Banknote,
        iconBg: "#00735C",
        nameKey: "notifications_payin_confirmed_name",
        descKey: "notifications_payin_confirmed_desc",
        titleKey: "notifications_bubble_payin_confirmed_title",
        bodyKey: "notifications_bubble_payin_confirmed_body",
      },
      {
        event: "payin_rejected",
        icon: XCircle,
        iconBg: "oklch(0.55 0.18 25)",
        nameKey: "notifications_payin_rejected_name",
        descKey: "notifications_payin_rejected_desc",
        titleKey: "notifications_bubble_payin_rejected_title",
        bodyKey: "notifications_bubble_payin_rejected_body",
      },
    ],
  },
  {
    titleKey: "notifications_group_payouts_title",
    descKey: "notifications_group_payouts_desc",
    notifications: [
      {
        event: "payout_paid",
        icon: CheckCircle2,
        iconBg: "#00735C",
        nameKey: "notifications_payout_paid_name",
        descKey: "notifications_payout_paid_desc",
        titleKey: "notifications_bubble_payout_paid_title",
        bodyKey: "notifications_bubble_payout_paid_body",
      },
      {
        event: "payout_failed",
        icon: XCircle,
        iconBg: "oklch(0.55 0.18 25)",
        nameKey: "notifications_payout_failed_name",
        descKey: "notifications_payout_failed_desc",
        titleKey: "notifications_bubble_payout_failed_title",
        bodyKey: "notifications_bubble_payout_failed_body",
      },
      {
        event: "wallet_balance_low",
        icon: AlertTriangle,
        iconBg: "oklch(0.65 0.16 75)",
        nameKey: "notifications_wallet_balance_low_name",
        descKey: "notifications_wallet_balance_low_desc",
        titleKey: "notifications_bubble_wallet_balance_low_title",
        bodyKey: "notifications_bubble_wallet_balance_low_body",
      },
    ],
  },
  {
    titleKey: "notifications_group_batches_title",
    descKey: "notifications_group_batches_desc",
    notifications: [
      {
        event: "batch_pending_approval",
        icon: ClipboardCheck,
        iconBg: "oklch(0.55 0.15 270)",
        nameKey: "notifications_batch_pending_name",
        descKey: "notifications_batch_pending_desc",
        titleKey: "notifications_bubble_batch_pending_title",
        bodyKey: "notifications_bubble_batch_pending_body",
      },
      {
        event: "batch_finished",
        icon: Layers,
        iconBg: "oklch(0.6 0.13 175)",
        nameKey: "notifications_batch_completed_name",
        descKey: "notifications_batch_completed_desc",
        titleKey: "notifications_bubble_batch_completed_title",
        bodyKey: "notifications_bubble_batch_completed_body",
      },
    ],
  },
  {
    titleKey: "notifications_group_breb_title",
    descKey: "notifications_group_breb_desc",
    notifications: [
      {
        event: "breb_payin_confirmed",
        icon: ArrowDownToLine,
        iconBg: "#00735C",
        nameKey: "notifications_breb_payment_name",
        descKey: "notifications_breb_payment_desc",
        titleKey: "notifications_bubble_breb_payment_title",
        bodyKey: "notifications_bubble_breb_payment_body",
      },
      {
        event: "breb_payout_paid",
        icon: Send,
        iconBg: "#00735C",
        nameKey: "notifications_breb_paid_name",
        descKey: "notifications_breb_paid_desc",
        titleKey: "notifications_bubble_breb_paid_title",
        bodyKey: "notifications_bubble_breb_paid_body",
      },
      {
        event: "breb_payout_failed",
        icon: XCircle,
        iconBg: "oklch(0.55 0.18 25)",
        nameKey: "notifications_breb_failed_name",
        descKey: "notifications_breb_failed_desc",
        titleKey: "notifications_bubble_breb_failed_title",
        bodyKey: "notifications_bubble_breb_failed_body",
      },
      {
        event: "breb_qr_payment_paid",
        icon: QrCode,
        iconBg: "oklch(0.58 0.12 220)",
        nameKey: "notifications_breb_qr_payment_name",
        descKey: "notifications_breb_qr_payment_desc",
        titleKey: "notifications_bubble_breb_qr_payment_title",
        bodyKey: "notifications_bubble_breb_qr_payment_body",
        comingSoon: true,
      },
    ],
  },
];

const ALL_NOTIFICATIONS = GROUPS.flatMap((group) => group.notifications);

const formatPhone = (phone: string) => (phone ? parsePhoneNumber(phone).formatInternational() : "");

/**
 * WhatsApp alerts for money coming in and going out of the organization's wallets: collections, payouts,
 * batches and Bre-B. They go to the organization's alert number, or to the phone of its own verification.
 */
export const PaymentNotificationsPage = () => {
  const { t } = useTranslations("wallets");
  const { t: tShared } = useTranslations("notificationPreferences");
  const { t: tGlobal } = useTranslations("global");
  const { user } = useAuth();
  const { wallets } = useRuntimeWallets();

  const {
    prefs,
    isLoading,
    hasNoAccess,
    canWrite,
    controlsDisabled,
    activeCount,
    totalTypes,
    pendingToggle,
    setPendingToggle,
    confirmPendingToggle,
    handleToggle,
    thresholdInputs,
    thresholdError,
    handleThresholdChange,
    handleThresholdBlur,
    saveRecipient,
    sendTest,
  } = usePaymentNotificationPreferences();
  const [testPhone, setTestPhone] = useState("");
  const [recipientDraft, setRecipientDraft] = useState<string | null>("");

  const breadcrumb = useMemo(
    () => [
      { label: tGlobal("sidebar_menu_wallets_home"), to: "/app/wallets" },
      { label: t("notifications_page_title") },
    ],
    [t, tGlobal],
  );

  const currencies = useMemo(
    () => [...new Set(wallets.map((wallet) => wallet.currency.toUpperCase()))].sort(),
    [wallets],
  );

  if (hasNoAccess) return <NoAccessView />;

  const pending = ALL_NOTIFICATIONS.find((definition) => definition.event === pendingToggle);
  const shownRecipient = prefs.phone ?? prefs.default_phone ?? "";
  // Keeping the default number on screen, or clearing a field that already follows it, saves nothing.
  const recipientChanged =
    recipientDraft !== null && recipientDraft !== shownRecipient && (recipientDraft || null) !== prefs.phone;
  const recipientHelp = prefs.phone
    ? t("notifications_recipient_help_custom")
    : prefs.default_phone
      ? t("notifications_recipient_help")
      : t("notifications_recipient_missing");
  const country = user?.country ?? undefined;

  const thresholdField = (
    <div className="flex flex-col gap-2">
      <div className="font-mono text-[10px] uppercase tracking-[0.08em] text-muted-foreground">
        {t("notifications_threshold_label")}
      </div>
      {currencies.length === 0 ? (
        <p className="text-[11px] text-muted-foreground">{t("notifications_threshold_no_wallets")}</p>
      ) : (
        <div className="flex flex-wrap gap-3">
          {currencies.map((currency) => (
            <label key={currency} className="flex items-center gap-2">
              <span className="w-10 font-mono text-[12px] text-muted-foreground">{currency}</span>
              <input
                type="number"
                inputMode="decimal"
                min={0}
                step="0.01"
                value={thresholdInputs[currency] ?? ""}
                onChange={(e) => handleThresholdChange(currency, e.target.value)}
                onBlur={() => void handleThresholdBlur()}
                disabled={controlsDisabled}
                aria-label={`${t("notifications_threshold_label")} ${currency}`}
                className={cn(
                  "h-9 w-36 rounded-md border bg-background px-2.5 font-mono text-[13px]",
                  thresholdError ? "border-destructive" : "border-border",
                )}
              />
            </label>
          ))}
        </div>
      )}
      {thresholdError ? (
        <p className="text-[11px] text-destructive">{thresholdError}</p>
      ) : (
        <p className="text-[11px] text-muted-foreground">{t("notifications_threshold_help")}</p>
      )}
    </div>
  );

  return (
    <ProductNotificationsLayout
      breadcrumb={breadcrumb}
      eyebrow={t("notifications_eyebrow")}
      title={t("notifications_page_title")}
      subtitle={t("notifications_page_subtitle")}
      heroTitle={t("notifications_hero_title")}
      heroSub={t("notifications_hero_sub")}
      heroAside={
        <div className="md:text-right">
          <div className="font-mono text-[9.5px] uppercase tracking-[0.06em] text-muted-foreground">
            {tShared("active_types")}
          </div>
          <div className="font-mono text-[18px] font-bold tabular-nums">
            {activeCount}/{totalTypes}
          </div>
        </div>
      }
      controls={
        isLoading ? undefined : (
          <div className="grid gap-4 md:grid-cols-2">
            <div className="flex flex-col gap-2">
              <NotificationPhoneField
                key={shownRecipient}
                label={t("notifications_recipient_label")}
                description={recipientHelp}
                defaultValue={shownRecipient}
                defaultCountry={country}
                disabled={!canWrite}
                onChange={setRecipientDraft}
              />
              {canWrite && recipientChanged && (
                <ColocaButton
                  type="button"
                  variant="primary"
                  size="sm"
                  className="self-start"
                  disabled={controlsDisabled}
                  onClick={() => void saveRecipient(recipientDraft ?? "")}
                >
                  {t("notifications_recipient_save")}
                </ColocaButton>
              )}
            </div>
            {canWrite && (
              <NotificationPhoneField
                label={tShared("test_phone_label")}
                description={tShared("test_phone_help")}
                defaultCountry={country}
                onChange={(phone) => setTestPhone(phone ?? "")}
              />
            )}
          </div>
        )
      }
      footnote={t("notifications_footnote")}
    >
      {GROUPS.map((group) => (
        <NotificationGroup key={group.titleKey} title={t(group.titleKey)} description={t(group.descKey)}>
          {group.notifications.map((definition) => (
            <NotificationTypeCard
              key={definition.event}
              icon={definition.icon}
              iconBg={definition.iconBg}
              availability={definition.comingSoon ? "coming_soon" : "available"}
              enabled={prefs[definition.event]}
              disabled={controlsDisabled}
              name={t(definition.nameKey)}
              desc={t(definition.descKey)}
              onToggle={handleToggle(definition.event)}
              onSendTest={canWrite ? () => sendTest(definition.event)(testPhone) : undefined}
              testRecipient={formatPhone(testPhone)}
              belowBody={definition.event === "wallet_balance_low" ? thresholdField : undefined}
              bubble={
                <WhatsAppBubble
                  header={t(definition.titleKey)}
                  body={renderNotificationBubbleBody(t(definition.bodyKey))}
                  footer={tShared("preview_footer")}
                  recipientHint={t("notifications_recipient_hint")}
                />
              }
            />
          ))}
        </NotificationGroup>
      ))}

      {/* Switching one ON starts messaging the organization, so the gesture is confirmed first. */}
      <ColocaAlertDialog
        trigger={<span style={{ display: "none" }} />}
        open={pending !== undefined}
        onOpenChange={(open) => {
          if (!open) setPendingToggle(null);
        }}
        media={<WhatsAppIcon size={28} />}
        title={pending ? t("notifications_enable_confirm_title", { name: t(pending.nameKey) }) : ""}
        description={
          <span className="block space-y-2 text-left">
            <span className="block">{t("notifications_enable_confirm_intro")}</span>
            <span className="block rounded-md border border-border bg-muted/50 px-3 py-2 text-[12px] text-foreground">
              {pending ? t(pending.descKey) : ""}
            </span>
            <span className="block text-[11.5px] text-muted-foreground">
              {t("notifications_enable_confirm_reversible")}
            </span>
          </span>
        }
        confirmLabel={t("notifications_enable_confirm_button")}
        cancelLabel={t("notifications_enable_confirm_cancel")}
        confirmVariant="primary"
        onConfirm={confirmPendingToggle}
        onCancel={() => setPendingToggle(null)}
      />
    </ProductNotificationsLayout>
  );
};
